export function formatPrice(price) {
  if (!price) {
    return '';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(price);
}

export function hideRelatedSection(container) {
  if (!container) {
    return;
  }

  container.style.display = 'none';
}

export function requestRelatedItems(productIds, requestExtra) {
  return window.Convermax.getRelatedItemsAsync(undefined, undefined, undefined, {
    ...requestExtra,
    productIds,
  }).then((items) => items.map((item) => ({ ...item, url: window.Convermax.getVehicleUrl(item.url) })));
}

export function updateRelatedContainers({
  relatedRequiredProducts,
  relatedAccessoriesProducts,
  customizationOptions,
  productIds,
  vehicle,
  updateSection,
  hideSection = hideRelatedSection,
}) {
  const hasProductIds = Array.isArray(productIds) ? productIds.length > 0 : !!productIds;
  const selectedVehicle =
    vehicle || (window.Convermax.isVehicleSelected() ? window.Convermax.getVehicle() : null);

  function updateContainer(container, requestExtra) {
    if (!container) {
      return;
    }

    const request = {};
    container.cmRelatedRequest = request;
    const isCurrent = () => container.isConnected && container.cmRelatedRequest === request;

    if (!hasProductIds) {
      hideSection(container);
      return;
    }

    requestRelatedItems(productIds, requestExtra)
      .then((items) => {
        if (isCurrent()) {
          updateSection(items, container);
          updateRelatedFitment(items, container, selectedVehicle, isCurrent);
        }
      })
      .catch(() => {
        if (isCurrent()) {
          hideSection(container);
        }
      });
  }

  updateContainer(relatedRequiredProducts, { isRelatedRequiredRequest: true });
  updateContainer(relatedAccessoriesProducts, { isRelatedAccessoriesRequest: true });
  updateContainer(customizationOptions, { isCustomizationOptionsRequest: true });
}

function updateRelatedFitment(items, container, vehicle, isCurrent) {
  const badges = container.querySelectorAll('[data-cm-related-fitment]');
  items.forEach(async (product, index) => {
    let verdict;
    try {
      if (product.Fitment === 'Non-Vehicle Products') {
        verdict = 'nonVehicleProducts';
      } else if (product.Fitment === 'No Fitment Data' || !product.Fitment) {
        verdict = 'unknown';
      } else if (!vehicle) {
        verdict = product.Fitment === 'Universal Fit' ? 'universal' : 'specify';
      } else {
        verdict = await window.Convermax.verifyFitmentAsync(String(product.id), vehicle);
      }
    } catch {
      return;
    }
    if (isCurrent() && badges[index]?.isConnected) {
      badges[index].innerHTML = getFitmentBadge(verdict);
      badges[index]
        .querySelector('button')
        ?.addEventListener('click', () => window.Convermax.openVehicleDialog());
    }
  });
}

function getFitmentBadge(state) {
  const labels = {
    yes: 'related_items_fits',
    no: 'related_items_does_not_fit',
    universal: 'related_items_universal',
    unknown: 'related_items_no_fitment_data',
    specify: 'related_items_add_vehicle',
  };
  if (!labels[state]) {
    return '';
  }
  const paths = {
    yes: 'M6 12l4 4 8-8',
    universal: 'M6 12l4 4 8-8',
    no: 'M7 7l10 10M17 7L7 17',
    specify: 'M12 5v9m0 3v2',
    unknown: 'M6 6l12 12',
  };
  const tag = state === 'specify' ? 'button' : 'span';
  return `
    <${tag}
      class="cm_related-fitment cm_related-fitment--${state}"
      ${state === 'specify' ? 'type="button"' : ''}
    >
      <svg class="cm_related-fitment_icon" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="12" />
        ${state === 'unknown' ? '<circle class="cm_related-fitment_symbol" cx="12" cy="12" r="8" />' : ''}
        <path class="cm_related-fitment_symbol" d="${paths[state]}" />
      </svg>
      <span>${window.Convermax.translations[labels[state]]}</span>
    </${tag}>
  `;
}

export function updateRelatedSection(items, container, renderCard) {
  if (items.length === 0) {
    container.style.display = 'none';
    return;
  }

  container.style.display = 'block';

  const currentSlider = container.querySelector('slideshow-component, .cm_related-section_carousel');
  if (!currentSlider) {
    return;
  }

  const sectionKey = container.id || 'related';
  currentSlider.outerHTML = `
    <div class="cm_related-section_carousel">
      <button type="button" data-cm-related-previous aria-label="Previous related product" disabled>‹</button>
      <div class="complementary-slider" role="list">
        ${items.map((item, index) => renderCard(item, index, sectionKey)).join('')}
      </div>
      <button type="button" data-cm-related-next aria-label="Next related product" disabled>›</button>
    </div>
  `;
  initializeRelatedSlider(
    container.querySelector('.complementary-slider'),
    container.querySelector('[data-cm-related-previous]'),
    container.querySelector('[data-cm-related-next]'),
  );
}

export function initializeRelatedSlider(slider, previousButton, nextButton, scrollbarThumb) {
  const getStep = () =>
    slider.children[1] ? slider.children[1].offsetLeft - slider.children[0].offsetLeft : slider.clientWidth;
  const updateSlider = () => {
    previousButton.disabled = slider.scrollLeft <= 1;
    nextButton.disabled = slider.scrollLeft + slider.clientWidth >= slider.scrollWidth - 1;
    if (scrollbarThumb) {
      scrollbarThumb.style.transform = `translateX(${Math.round(slider.scrollLeft / getStep()) * 100}%)`;
    }
  };
  previousButton.addEventListener('click', () => {
    slider.scrollBy({ left: -getStep(), behavior: 'smooth' });
  });
  nextButton.addEventListener('click', () => {
    slider.scrollBy({ left: getStep(), behavior: 'smooth' });
  });
  slider.addEventListener('scroll', updateSlider);
  const resizeObserver = new ResizeObserver(() => {
    if (slider.isConnected) {
      updateSlider();
    } else {
      resizeObserver.disconnect();
    }
  });
  resizeObserver.observe(slider);
  updateSlider();
}
